import { labelComponents } from '../annotation/raster';
import { autoDiffers } from './detect';
import { bandMask, previewFrom } from './filters';
import { eyepieceRing } from './focus';

/*
 * A rough count of what a live microscope picture shows, for display only: pure
 * code on the small frame the frame worker already reads (frame-worker.js), and
 * on the main thread where there is no worker. Nothing here is stored, and it
 * gives no sizes: a live camera has no calibrated scale.
 *
 * It finds particles as "Auto (colourless spores)" does (detect.autoDiffers):
 * whatever differs from the field's own grey level. That reads dark, light and
 * colourless spores alike, and a clean field gives none, because the band is at
 * least five times the field's noise. Otsu's threshold would split a clean field
 * in two. The eyepiece's dark surround (focus.eyepieceRing) is left out of the
 * field's level and of the picture, and a particle cut by it or by the frame is
 * dropped, as analyseMask drops one cut by the frame. A huge particle reaching the
 * frame means the "field" wasn't the field (a dense deposit whose spores fill the
 * histogram's peak, or uneven light), so it says it can't tell rather than count.
 *
 * Unlike detect.grayscale it doesn't blur: this frame is already about a quarter
 * of the camera's width, and another pixel of blur here would be four there.
 *
 * Budget: a frame's whole job fits the SAMPLE_MS between samples. One that runs
 * over is skipped, and the next LIVE_REST_FRAMES are too, so a slow phone keeps
 * its focus meter and the count says it isn't keeping up. Nothing queues.
 *
 * None of these numbers has been measured on a real slide through a live camera.
 */
//: the frame pipeline's SAMPLE_MS (components/camera/useFrameSampler.js), pinned by liveCount.test.js
export const LIVE_BUDGET_MS = 200;
export const LIVE_REST_FRAMES = 4;
//: frame pixels: about 3 x 2, the least that can be told from dust at this size
export const LIVE_MIN_AREA_PX = 6;
//: a share of the frame: anything larger is a clump or the slide's shading, not one spore
export const LIVE_MAX_AREA_SHARE = 0.05;
//: the count shown is the median of this many frames, so it doesn't flicker five times a second
export const LIVE_WINDOW = 5;
//: no fresh count for this long and the one shown is out of date
export const LIVE_STALE_MS = 1000;
export const UNCLEAR = 'unclear';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** How many particles the picture holds; UNCLEAR if it can't tell; null if `over()` says the time ran out on the way. */
export function countParticles(gray, w, h, { over = () => false } = {}) {
  if (over()) return null;
  const ring = eyepieceRing(gray, w, h);
  const field = gray.filter((_, i) => !ring[i]);
  if (!field.length) return 0;
  const { min, max } = autoDiffers(field);
  if (over()) return null;
  const allow = ring.map(v => (v ? 0 : 1));
  const { mask } = previewFrom(bandMask(gray, w, h, { min, max, outside: true, allow }), w, h, { allow });
  if (over()) return null;
  const { labels, sizes, touchesBorder, count } = labelComponents(mask, w, h, 8);
  const cut = new Uint8Array(count + 1);
  for (let i = 0; i < ring.length; i += 1) {
    if (!ring[i]) continue;
    const x = i % w;
    if (x > 0) cut[labels[i - 1]] = 1;
    if (x < w - 1) cut[labels[i + 1]] = 1;
    if (i >= w) cut[labels[i - w]] = 1;
    if (i + w < ring.length) cut[labels[i + w]] = 1;
  }
  const largest = LIVE_MAX_AREA_SHARE * w * h;
  let found = 0;
  for (let l = 1; l <= count; l += 1) {
    if (touchesBorder[l] && sizes[l] > largest) return UNCLEAR;
    if (!touchesBorder[l] && !cut[l] && sizes[l] >= LIVE_MIN_AREA_PX && sizes[l] <= largest) found += 1;
  }
  return found;
}

/**
 * One running camera's counter: (gray, w, h, startedAt) -> {count},
 * {count: null, unclear: true} or {count: null, skipped: true}. `startedAt` is
 * when this frame's job began, so the time already spent on it counts.
 */
export function makeLiveCounter({ clock = now, budgetMs = LIVE_BUDGET_MS, restFrames = LIVE_REST_FRAMES } = {}) {
  let resting = 0;
  return (gray, w, h, startedAt = clock()) => {
    if (resting > 0) {
      resting -= 1;
      return { count: null, skipped: true };
    }
    const over = () => clock() - startedAt > budgetMs;
    const count = countParticles(gray, w, h, { over });
    if (count === null || over()) {
      resting = restFrames;
      return { count: null, skipped: true };
    }
    return count === UNCLEAR ? { count: null, unclear: true } : { count };
  };
}

export const FRESH_LIVE_COUNT = Object.freeze({ recent: [], count: null, unclear: false, lastAt: null, stale: false });

function median(values) {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

/** The count to show after one more sample: `spores` is the counter's answer, or absent when none was asked for. */
export function liveCountStep(state, spores, at) {
  if (!spores) return state;
  if (!spores.skipped) {
    // A frame that couldn't tell holds its place: most of the window has to be sure of a number.
    const recent = [...state.recent, spores.unclear ? null : spores.count].slice(-LIVE_WINDOW);
    const counts = recent.filter(v => v !== null);
    const unclear = counts.length * 2 < recent.length;
    return { recent, count: unclear ? null : median(counts), unclear, lastAt: at, stale: false };
  }
  const stale = state.lastAt === null || at - state.lastAt > LIVE_STALE_MS;
  return stale === state.stale ? state : { ...state, stale };
}
